'use strict';

// Keep diagnostic text; redact credential assignments without discarding the reason.
function redactDiagnostic(message) {
  return String(message || '')
    .replace(/\b(Bearer\s+|Basic\s+)[A-Za-z0-9._~+/=-]+/gi, '$1[Redacted]')
    .replace(/(["']?(?:password|pwd|secret|client[_-]?secret|api[_-]?key|access[_-]?token|refresh[_-]?token|session[_-]?token|token|authorization|x-api-key|(?:aws[_-]?)?secret[_-]?access[_-]?key|(?:aws[_-]?)?access[_-]?key[_-]?id|x-amz-(?:signature|credential|security-token))["']?\s*[:=]\s*)(?:"(?:""|\\.|[^"\\])*"|'(?:''|\\.|[^'\\])*'|[^\r\n,;}&]+)/gi, '$1[Redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[Redacted]')
    .replace(/(\b[a-z][a-z0-9+.-]*:\/\/[^\s/:@]+:)[^\s/@]+@/gi, '$1[Redacted]@');
}

function diagnosticFields(values) {
  const allowed = /^(ContactId|InitialContactId|ConnectCallId|CallId|ContactEventId|CampaignId|CampusId|SourceId|ProgramId|Mod|Job|JobAction|Operation|Field|CorrelationId|RequestId|Stage|Outcome|StatusCode|RetryAttempt|RetryDelaySeconds)$/i;
  return Object.fromEntries(Object.entries(values || {}).filter(([key, value]) =>
    allowed.test(key) && ['string', 'number', 'boolean'].includes(typeof value))
    .map(([key, value]) => [key, typeof value === 'string' ? redactDiagnostic(value) : value]));
}


function safeFrame(frame) {
  const filename = String(frame.filename || '').split(/[?#]/)[0].replace(/\\/g, '/').split('/').pop();
  return {
    filename: filename && /\.(?:[cm]?js|tsx?|bundle)$/.test(filename) ? `app:///${filename}` : undefined,
    function: /^[\w.$<> ]{1,120}$/.test(frame.function || '') ? frame.function : undefined,
    lineno: frame.lineno,
    colno: frame.colno,
    in_app: frame.in_app,
  };
}

function scrubEvent(event) {
  const trace = event.contexts && event.contexts.trace;
  const clean = {
    event_id: event.event_id, timestamp: event.timestamp, platform: event.platform,
    level: event.level, release: event.release, environment: event.environment,
    sdk: event.sdk, type: event.type,
    extra: diagnosticFields(event.extra), tags: diagnosticFields(event.tags),
    user: { ip_address: '0.0.0.0' },
    contexts: trace ? { trace: { trace_id: trace.trace_id, span_id: trace.span_id,
      parent_span_id: trace.parent_span_id, op: 'client.operation', status: trace.status } } : {},
  };
  if (event.message || event.logentry) clean.message = 'Client error';
  if (event.exception) {
    clean.exception = { values: (event.exception.values || []).map(exception => ({
      type: /^[\w.$]{1,160}$/.test(exception.type || '') ? exception.type : 'ClientError',
      value: redactDiagnostic(exception.value),
      mechanism: exception.mechanism ? { type: "generic", handled: exception.mechanism.handled } : undefined,
      stacktrace: exception.stacktrace ? { frames: (exception.stacktrace.frames || []).map(safeFrame) } : undefined,
    })) };
  }
  return clean;
}

function scrubSpan(span) {
  return { trace_id: span.trace_id, span_id: span.span_id, parent_span_id: span.parent_span_id,
    start_timestamp: span.start_timestamp, timestamp: span.timestamp, status: span.status,
    op: 'client.operation', description: 'Client operation', data: {} };
}

function scrubTransaction(event) {
  return { ...scrubEvent(event), transaction: 'client.lifecycle', start_timestamp: event.start_timestamp,
    spans: (event.spans || []).map(scrubSpan), transaction_info: { source: 'custom' } };
}

function isEnabled(config, development) {
  return !development && config.enabled === true
    && typeof config.dsn === 'string' && /^https:\/\/[^:@/?#]+@[^/?#]+\/\d+$/.test(config.dsn)
    && !['development', 'testing', ''].includes(String(config.environment || '').trim().toLowerCase());
}

function startClientTelemetry(sdk, config, runtime = {}) {
  const disabled = { ready() {}, captureException() {} };
  if (!isEnabled(config, runtime.development)) return disabled;
  const component = ['browser', 'main', 'renderer', 'mobile'].includes(runtime.component) ? runtime.component : 'browser';
  const attributes = { 'client.component': component };
  const started = Date.now();
  try {
    sdk.init({
      ...runtime.sdkOptions,
      dsn: config.dsn, environment: config.environment.trim().toLowerCase(), release: config.release || undefined,
      sendDefaultPii: false, sendClientReports: false, debug: false, enableLogs: false,
      autoSessionTracking: false,
      traceLifecycle: 'static',
      integrations: integrations => {
        const selected = typeof runtime.sdkOptions?.integrations === 'function'
          ? runtime.sdkOptions.integrations(integrations) : integrations;
        return selected.filter(integration => !['BrowserSession', 'CultureContext', 'MainProcessSession', 'SentryMinidump', 'ElectronMinidump'].includes(integration.name));
      },
      tracesSampleRate: typeof config.tracesSampleRate === 'number' && Number.isFinite(config.tracesSampleRate)
        && config.tracesSampleRate >= 0 && config.tracesSampleRate <= 1 ? config.tracesSampleRate : 0.1,
      tracePropagationTargets: [],
      beforeSend: scrubEvent, beforeSendTransaction: scrubTransaction, beforeSendSpan: scrubSpan,
      beforeBreadcrumb: () => null, beforeSendLog: () => null,
      beforeSendMetric: metric => {
        if (config.metricsEnabled === false || !['client.starts', 'client.ready.duration'].includes(metric.name)) return null;
        return { ...metric, attributes };
      },
    });
    const span = sdk.startInactiveSpan && sdk.startInactiveSpan({ name: 'client.ready', op: 'ui.load', forceTransaction: true });
    const withStartupSpan = callback => span && sdk.withActiveSpan ? sdk.withActiveSpan(span, callback) : callback();
    if (config.metricsEnabled !== false && sdk.metrics) withStartupSpan(() => sdk.metrics.count('client.starts', 1, { attributes }));
    let ready = false;
    return {
      ready() {
        if (ready) return;
        ready = true;
        try {
          if (config.metricsEnabled !== false && sdk.metrics) withStartupSpan(() => sdk.metrics.distribution('client.ready.duration', Math.max(0, Date.now() - started), { unit: 'millisecond', attributes }));
          if (span) span.end();
        } catch { /* Telemetry must not interrupt application startup. */ }
      },
      captureException(error) { try { if (sdk.captureException) sdk.captureException(error); } catch {} },
    };
  } catch {
    return disabled;
  }
}

module.exports = { scrubEvent, scrubSpan, scrubTransaction, isEnabled, startClientTelemetry };
